// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Card } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { Appointment, Encounter, Patient, Reference } from '@medplum/fhirtypes';
import { Loading, useResource } from '@medplum/react';
import type { JSX } from 'react';
import { useParams } from 'react-router';
import { ConcludeSessionModal } from '../../components/appointment/ConcludeSessionModal';
import { CoordinatorNotePage } from '../../components/coordinator/CoordinatorNotePage';
import encounterStyles from '../../components/encounter/EncounterNoteRefactoredShell.module.css';
import { EncounterWorkspaceShell } from '../../components/encounter/EncounterWorkspaceShell';
import { ScheduleNextAppointmentModal } from '../../components/encounter/ScheduleNextAppointmentModal';
import { useRole } from '../../hooks/useRole';
import { showErrorNotification } from '../../utils/notifications';
import { hasPermission } from '../../utils/permissions';
import { MedicalInfoTab } from '../patient/medical-info/MedicalInfoTab';
import { ProfileTab } from '../patient/profile/ProfileTab';

interface EncounterPatientWorkspacePageProps {
  currentTab: 'profile' | 'medical-info' | 'coordinator-note';
}

export function EncounterPatientWorkspacePage({ currentTab }: EncounterPatientWorkspacePageProps): JSX.Element | null {
  const { encounterId } = useParams();
  const encounter = useResource<Encounter>(
    encounterId
      ? {
          reference: `Encounter/${encounterId}`,
        }
      : undefined
  );
  const patientReference = encounter?.subject as Reference<Patient> | undefined;
  const patient = useResource(patientReference);
  const appointment = useResource<Appointment>(encounter?.appointment?.at(-1));
  const [concludeOpened, { open: openConclude, close: closeConclude }] = useDisclosure(false);
  const [scheduleOpened, { open: openSchedule, close: closeSchedule }] = useDisclosure(false);
  const { role, isAdmin } = useRole();
  const canSchedule = hasPermission({ role, permission: 'schedule-appointment', isAdmin });

  if (!encounterId) {
    showErrorNotification('Encounter ID not found');
    return null;
  }

  if (!encounter || !patient) {
    return <Loading />;
  }

  const content = (): JSX.Element => {
    if (currentTab === 'profile') {
      return <ProfileTab />;
    }
    if (currentTab === 'medical-info') {
      return <MedicalInfoTab encounter={encounter} appointment={appointment} />;
    }
    return <CoordinatorNotePage />;
  };

  return (
    <>
      <EncounterWorkspaceShell
        appointment={appointment}
        canSchedule={canSchedule}
        currentTab={currentTab}
        encounter={encounter}
        onConclude={openConclude}
        onOpenSchedule={openSchedule}
        patient={patient}
      >
        <Card className={encounterStyles.card} p="md">
          {content()}
        </Card>
      </EncounterWorkspaceShell>

      <ConcludeSessionModal
        opened={concludeOpened}
        onClose={closeConclude}
        encounter={encounter}
        appointment={appointment}
        onBeforeConclude={async () => undefined}
        onConcluded={closeConclude}
      />

      <ScheduleNextAppointmentModal opened={scheduleOpened} onClose={closeSchedule} patient={patient} />
    </>
  );
}
